import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { uniqueId } from 'lodash'

type CloseFunction = () => void
type GlobalPortalContextType = (addElement: (closeFn: CloseFunction) => React.ReactNode) => CloseFunction

const GlobalPortalContext = createContext<GlobalPortalContextType | null>(null)

export const useGlobalPortal = () => useContext(GlobalPortalContext)

/**
 * A React context provider component that manages global elements and allows
 * dynamically adding and removing React nodes to a global container.
 *
 * @param {Object} props - The props for the provider component.
 * @param {React.ReactNode} props.children - The child components to render within the provider.
 *
 * @returns {JSX.Element} The provider component that wraps its children and renders global elements.
 *
 * @remarks
 * - This component uses a `useState` hook to maintain a list of elements and their unique keys.
 * - The `renderElement` function allows adding a new element to the global container. It accepts a callback
 *   that provides a `closeFn` function to remove the element.
 * - The elements are rendered using `ReactDOM.createPortal` into a DOM element with the ID `globalElement`.
 *
 * @example
 * ```tsx
 * const renderElement = useContext(GlobalElementContext);
 *
 * const handleOpen = () => {
 *   renderElement((closeFn) => (
 *     <div>
 *       <p>Global Element Content</p>
 *       <button onClick={closeFn}>Close</button>
 *     </div>
 *   ));
 * };
 *
 * return <button onClick={handleOpen}>Open Global Element</button>;
 * ```
 */
export const GlobalPortalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [elements, setElements] = useState<{ key: string, value: React.ReactNode, container: HTMLDivElement }[]>([])
  const elementsRef = useRef(elements)
  elementsRef.current = elements

  const renderElement = useCallback((addElement: (closeFn: () => void) => React.ReactNode) => {
    const key = uniqueId()
    const container = document.createElement('div')
    document.getElementById('globalElement')?.appendChild(container)

    const closeFunction = () => {
      const newElements = elementsRef.current.filter((el) => el.key !== key)
      setElements(newElements)
      container.remove()
    }

    const newElement = addElement(closeFunction)
    setElements((currentElements) => [...currentElements, { key, value: newElement, container }])

    return closeFunction
  }, [])

  useEffect(() => {
    initGlobalPortal(renderElement)
  }, [renderElement])

  return (
    <GlobalPortalContext.Provider value={renderElement}>
      {children}
      {elements.map((element) => {
        return ReactDOM.createPortal(
          element.value,
          element.container,
          element.key,
        )
      })}
    </GlobalPortalContext.Provider>
  )
}

// eslint-disable-next-line camelcase
export let UNSAFE_renderElement: GlobalPortalContextType = null
export function initGlobalPortal(renderElement: GlobalPortalContextType) {
  // eslint-disable-next-line camelcase
  UNSAFE_renderElement = renderElement

  return renderElement
}
